import * as THREE from 'three';
import type { RenderMode } from '@/shared/store/viewer-store';
import { FurnitureEntityVisibility, type FurnitureVisibilityMeta } from './furnitureVisibility';
import { RENDER_MODE_DEPTH_WRITE_AT, type RenderModeMorph } from './lib/renderModeMorph';

function recordFrom(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function finiteNumber(value: unknown): number | null {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function materialList(material: THREE.Material | THREE.Material[] | undefined): THREE.Material[] {
  if (!material) return [];
  return Array.isArray(material) ? material : [material];
}

function isBasisEdgeOverlay(object: THREE.Object3D): object is THREE.LineSegments {
  return Boolean(object.userData?.basisEdgeOverlay) && (object as THREE.LineSegments).isLineSegments === true;
}

function applyBasisEdgeOverlayMode(line: THREE.LineSegments, mode: RenderMode): void {
  const modeName = String(mode || 'solid');
  const hardware = Boolean(line.userData?.edgeHardware);
  const glass = Boolean(line.userData?.edgeGlass);
  const cylinder = Boolean(line.userData?.edgeCylinder);
  const contractObject = new FurnitureRenderObject(line);
  const state = contractObject.visibility.materialStateForMode(mode);
  const visible = state.opacity > 0.001;
  const xrayLike = modeName === 'xray' || modeName === 'wireframe';
  line.visible = visible;
  line.renderOrder = (contractObject.renderOrder() || 0) + (xrayLike ? 160 : 8) + (hardware ? 20 : 0);
  for (const mat of materialList(line.material as THREE.Material | THREE.Material[] | undefined)) {
    const lineMat = mat as THREE.LineBasicMaterial;
    // Покой (solid) — это ПРЕЗЕНТАЦИЯ, а не чертёж: кромка даёт определённость
    // бледным поверхностям, но не рисует сетку технической графики. Было
    // 0.76 почти-чёрным (0x0f1115) на каждом меше — сцена читалась как
    // проволочный каркас. Техрежимы (flat/xray/wireframe) по-прежнему громкие.
    const ink =
      hardware ? (xrayLike ? 0x22272f : 0x4a443c)
      : glass ? 0x1f2937
      : xrayLike ? 0x0f1115
      : 0x574f43;
    lineMat.color.setHex(ink);
    lineMat.transparent = true;
    lineMat.opacity =
      modeName === 'wireframe' ? (hardware ? 0.78 : 0.9)
      : modeName === 'xray' ? (hardware ? 0.7 : 0.84)
      : modeName === 'flat' ? (hardware ? 0.56 : 0.82)
      : (hardware ? 0.14 : glass ? 0.3 : 0.16);
    if (cylinder && !xrayLike) {
      lineMat.opacity *= 0.72;
    }
    lineMat.depthTest = !xrayLike;
    lineMat.depthWrite = false;
    lineMat.toneMapped = false;
    lineMat.needsUpdate = true;
  }
}

type FlatStash = {
  map: THREE.Texture | null;
  normalMap: THREE.Texture | null;
  roughnessMap: THREE.Texture | null;
  metalnessMap: THREE.Texture | null;
  aoMap: THREE.Texture | null;
  emissiveMap: THREE.Texture | null;
  roughness: number;
  metalness: number;
  envMapIntensity: number;
  emissive: THREE.Color;
  emissiveIntensity: number;
};

/**
 * BAZIS-style flat shading as a pure material transform — visible even on
 * colour-only materials (no texture maps to drop).  Drops every PBR map and
 * kills the glossy/reflective response, then lifts each face toward its flat
 * albedo via an emissive term so the surface reads as one matte colour
 * regardless of light angle.  The original PBR state is stashed on the
 * material so leaving flat mode restores it without reloading the model.
 */
function applyFlatShading(std: THREE.MeshStandardMaterial, flat: boolean): void {
  const ext = std as THREE.MeshStandardMaterial & { __flatStash?: FlatStash };
  if (flat) {
    if (!ext.__flatStash) {
      ext.__flatStash = {
        map: std.map,
        normalMap: std.normalMap,
        roughnessMap: std.roughnessMap,
        metalnessMap: std.metalnessMap,
        aoMap: std.aoMap,
        emissiveMap: std.emissiveMap,
        roughness: std.roughness,
        metalness: std.metalness,
        envMapIntensity: std.envMapIntensity ?? 1,
        emissive: std.emissive.clone(),
        emissiveIntensity: std.emissiveIntensity,
      };
    }
    std.map = null;
    std.normalMap = null;
    std.roughnessMap = null;
    std.metalnessMap = null;
    std.aoMap = null;
    std.emissiveMap = null;
    std.roughness = 1;
    std.metalness = 0;
    std.envMapIntensity = 0;
    std.emissive.copy(std.color);
    std.emissiveIntensity = 0.55;
    return;
  }
  const stash = ext.__flatStash;
  if (!stash) return;
  std.map = stash.map;
  std.normalMap = stash.normalMap;
  std.roughnessMap = stash.roughnessMap;
  std.metalnessMap = stash.metalnessMap;
  std.aoMap = stash.aoMap;
  std.emissiveMap = stash.emissiveMap;
  std.roughness = stash.roughness;
  std.metalness = stash.metalness;
  std.envMapIntensity = stash.envMapIntensity;
  std.emissive.copy(stash.emissive);
  std.emissiveIntensity = stash.emissiveIntensity;
  delete ext.__flatStash;
}

export type RenderObjectVisibilityInput = {
  layerMode: 'body' | 'details' | 'tech';
  renderMode: RenderMode;
  openedPanels: Record<string, number>;
  apartmentScene?: boolean;
  userAllowed?: boolean;
};

/**
 * Object-scene adapter.
 *
 * The renderer supplies only the current view mode and the Object3D.  All
 * product semantics come from the object's domain contracts stamped on the
 * entity: material passport, renderModeContract and visibilityContract.
 */
export class FurnitureRenderObject {
  readonly object: THREE.Object3D;

  constructor(object: THREE.Object3D) {
    this.object = object;
  }

  get domain(): Record<string, unknown> {
    return recordFrom(this.object.userData?.domain);
  }

  get meta(): FurnitureVisibilityMeta {
    return {
      type: this.object.userData?.type,
      moduleId: this.object.userData?.moduleId,
      render: recordFrom(this.object.userData?.render),
      domain: this.domain,
    };
  }

  get visibility(): FurnitureEntityVisibility {
    return new FurnitureEntityVisibility(this.meta);
  }

  private get renderModeContract(): Record<string, unknown> {
    return recordFrom(this.domain.renderModeContract);
  }

  renderOrder(): number {
    return finiteNumber(this.renderModeContract.renderOrder) ?? 0;
  }

  /**
   * `morph` — доля перехода между режимами, посчитанная `lib/renderModeMorph.ts`.
   *
   * ВТОРОЙ РУКИ НА МАТЕРИАЛАХ НЕТ НАМЕРЕННО. Целевые числа по-прежнему даёт контракт объекта;
   * переход только ВЕДЁТ прозрачность к ним от того значения, которое было в момент смены
   * режима. Значение запоминается на самом материале и помечается меткой перехода
   * (`startedMs`): передумали на полпути — запоминается нынешнее, промежуточное, и картинка
   * не прыгает к началу. Донор: display-mode-transition-controller.ts.
   *
   * Каркас и «плоско» не смешиваются: между сеткой и заливкой нет половины. Они берут режим
   * цели сразу — морфит ровно то, что морфится, прозрачность.
   */
  applyMaterialMode(mode: RenderMode, morph?: RenderModeMorph) {
    const renderOrder = this.renderOrder();
    const blending = Boolean(morph) && !morph!.done;
    this.object.traverse((child) => {
      if (isBasisEdgeOverlay(child)) {
        applyBasisEdgeOverlayMode(child, mode);
        return;
      }
      const mesh = child as THREE.Mesh;
      if (!mesh.isMesh || mesh.userData?.analysisMarker) return;
      const childObject = new FurnitureRenderObject(mesh);
      const state = childObject.visibility.materialStateForMode(mode);
      mesh.renderOrder = childObject.renderOrder() || renderOrder;
      for (const mat of materialList(mesh.material)) {
        const std = mat as THREE.MeshStandardMaterial & {
          wireframe?: boolean;
          alphaHash?: boolean;
          alphaToCoverage?: boolean;
          forceSinglePass?: boolean;
          __modeMorph?: { startedMs: number; fromOpacity: number };
        };
        let opacity = state.opacity;
        let transparent = state.transparent;
        let depthWrite = state.depthWrite;
        const nativeAlpha = std.userData?.nativeAlphaState as
          | { opacity: number; transparent: boolean; alphaTest: number; depthWrite: boolean }
          | undefined;
        const alphaMode = childObject.domain.sourceAlphaMode;
        const alphaCutoff = alphaMode === 'MASK'
          ? Number(childObject.domain.sourceAlphaCutoff ?? nativeAlpha?.alphaTest ?? 0.5)
          : (nativeAlpha?.alphaTest ?? 0);
        if (mode === 'solid' && nativeAlpha) {
          opacity = nativeAlpha.opacity * state.opacity;
          transparent = nativeAlpha.transparent || state.transparent;
          depthWrite = nativeAlpha.depthWrite && state.depthWrite;
        }
        /* Публикационный пин глубины (nativeGlbScene.stabilizeCoplanarPairs):
         * копланарные грани исходника получают polygonOffset, иначе печь/духовка
         * в нише мерцают. Пин переживает смену режима рендера — разрешается
         * здесь, а не только при построении сцены. */
        const offsetPin = std.userData?.polygonOffsetPin as
          | { factor?: number; units?: number }
          | undefined;
        const wantOffset = offsetPin !== undefined;
        const wantFactor = wantOffset ? Number(offsetPin.factor ?? 1) : 0;
        const wantUnits = wantOffset ? Number(offsetPin.units ?? 2) : 0;
        if (blending) {
          const memo = std.__modeMorph;
          const from = memo && memo.startedMs === morph!.startedMs ? memo.fromOpacity : std.opacity;
          if (!memo || memo.startedMs !== morph!.startedMs) std.__modeMorph = { startedMs: morph!.startedMs, fromOpacity: from };
          opacity = from + (opacity - from) * morph!.progress;
          /* Пока обе стороны перехода полупрозрачны, смесь обязана быть прозрачной, иначе
             доля не видна вовсе. Запись глубины возвращается только после середины. */
          transparent = true;
          depthWrite = depthWrite && morph!.progress >= RENDER_MODE_DEPTH_WRITE_AT;
        } else if (std.__modeMorph) {
          delete std.__modeMorph;
        }
        /* `needsUpdate` пересобирает программу шейдера. На кадре перехода это стоило бы
           пересборки ВСЕЙ сцены шестьдесят раз в секунду, поэтому флаг ставится только
           когда изменилось то, от чего программа зависит. */
        const programDirty = std.wireframe !== state.wireframe
          || std.transparent !== transparent
          || std.alphaTest !== alphaCutoff
          || std.alphaHash === true
          || std.alphaToCoverage === true
          || std.forceSinglePass !== true
          || std.polygonOffset !== wantOffset;
        std.wireframe = state.wireframe;
        std.transparent = transparent;
        std.opacity = opacity;
        std.depthWrite = depthWrite;
        std.depthTest = true;
        std.alphaTest = alphaCutoff;
        std.alphaHash = false;
        std.alphaToCoverage = false;
        std.forceSinglePass = true;
        std.polygonOffset = wantOffset;
        std.polygonOffsetFactor = wantFactor;
        std.polygonOffsetUnits = wantUnits;
        const flatDirty = Boolean((std as { __flatStash?: unknown }).__flatStash) !== (mode === 'flat');
        applyFlatShading(std, mode === 'flat');
        if (programDirty || flatDirty) std.needsUpdate = true;
      }
    });
  }

  applyVisibility(input: RenderObjectVisibilityInput): boolean {
    const desired = Boolean(input.userAllowed ?? true)
      && this.visibility.shouldShowSceneLayer(
        input.layerMode,
        input.renderMode,
        input.openedPanels,
        { apartmentScene: Boolean(input.apartmentScene) },
      );
    this.object.visible = desired;
    return desired;
  }
}
